import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AdminSettings.module.css";
import {
  CAMPAIGN_TICKET_TAB_OPTIONS,
  CampaignDatePreset,
  CampaignRecipientFilterState,
  CampaignTeamOption,
  CampaignTicketTab,
  getNext7DaysEndStr,
  getTodayStr,
  getTomorrowStr,
  hasActiveCampaignRecipientFilters,
} from "./campaignRecipientFilters";

interface CampaignRecipientFilterPanelProps {
  filters: CampaignRecipientFilterState;
  onChange: (next: CampaignRecipientFilterState) => void;
  teamsList: CampaignTeamOption[];
  teamsLoading?: boolean;
  matchingCount: number | null;
  eligibleEmailCount: number | null;
  eligiblePushCount: number | null;
  isResolving: boolean;
  resolveError?: string | null;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  channel: "email" | "push";
}

const CampaignRecipientFilterPanel: React.FC<CampaignRecipientFilterPanelProps> = ({
  filters,
  onChange,
  teamsList,
  teamsLoading = false,
  matchingCount,
  eligibleEmailCount,
  eligiblePushCount,
  isResolving,
  resolveError,
  onApplyFilters,
  onClearFilters,
  channel,
}) => {
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [teamSearchText, setTeamSearchText] = useState("");
  const teamDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target as Node)) {
        setTeamDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const update = (patch: Partial<CampaignRecipientFilterState>) => {
    onChange({ ...filters, ...patch });
  };

  const applyDatePreset = (preset: Exclude<CampaignDatePreset, null | "custom">) => {
    onChange({
      ...filters,
      datePreset: preset,
      customStartDate: "",
      customEndDate: "",
    });
  };

  const selectedTeamLabel =
    teamsList.find((team) => team.value === filters.selectedTeam)?.label || "All teams";

  const filteredTeamsList = useMemo(() => {
    const query = teamSearchText.trim().toLowerCase();
    if (!query) return teamsList;
    return teamsList.filter(
      (team) =>
        team.label.toLowerCase().includes(query) || team.value.toLowerCase().includes(query),
    );
  }, [teamSearchText, teamsList]);

  const handleSelectTeam = (teamValue: string) => {
    update({ selectedTeam: teamValue });
    setTeamDropdownOpen(false);
    setTeamSearchText("");
  };

  const dateLabel = (() => {
    if (filters.datePreset === "today") return "Today";
    if (filters.datePreset === "tomorrow") return "Tomorrow";
    if (filters.datePreset === "next7days") return "Next 7 days";
    if (filters.datePreset === "custom" && filters.customStartDate && filters.customEndDate) {
      return `${filters.customStartDate} to ${filters.customEndDate}`;
    }
    return "Any event date";
  })();

  const eligibleCount = channel === "email" ? eligibleEmailCount : eligiblePushCount;

  return (
    <div className={styles.campaignFilterPanel}>
      <p className={styles.campaignFilterHint}>
        Filter by a user&apos;s My Teams assignment and/or tickets in Manage Tickets statuses.
        Event date filters apply to ticket event dates.
      </p>

      <div className={styles.campaignFilterGrid}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>My Teams</label>
          <div className={styles.campaignTeamFilterContainer} ref={teamDropdownRef}>
            <button
              type="button"
              className={`${styles.campaignTeamFilterBtn} ${filters.selectedTeam ? styles.campaignTeamFilterBtnActive : ""}`}
              onClick={() => setTeamDropdownOpen((open) => !open)}
              disabled={teamsLoading}
            >
              <span className={styles.campaignTeamFilterBtnLabel}>
                {teamsLoading ? "Loading teams..." : selectedTeamLabel}
              </span>
              {filters.selectedTeam && (
                <span
                  className={styles.campaignTeamFilterClear}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectTeam("");
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectTeam("");
                    }
                  }}
                >
                  ×
                </span>
              )}
              <span className={styles.campaignTeamFilterChevron}>{teamDropdownOpen ? "▴" : "▾"}</span>
            </button>

            {teamDropdownOpen && (
              <div className={styles.campaignTeamFilterMenu}>
                <div className={styles.campaignTeamFilterSearchRow}>
                  <input
                    type="text"
                    className={styles.campaignTeamFilterSearchInput}
                    placeholder="Search teams..."
                    value={teamSearchText}
                    onChange={(e) => setTeamSearchText(e.target.value)}
                    autoFocus
                  />
                  {teamSearchText && (
                    <button
                      type="button"
                      className={styles.campaignTeamFilterSearchClear}
                      onClick={() => setTeamSearchText("")}
                      aria-label="Clear team search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className={styles.campaignTeamFilterList}>
                  <button
                    type="button"
                    className={`${styles.campaignTeamFilterOption} ${!filters.selectedTeam ? styles.campaignTeamFilterOptionActive : ""}`}
                    onClick={() => handleSelectTeam("")}
                  >
                    All teams
                  </button>
                  {filteredTeamsList.length > 0 ? (
                    filteredTeamsList.map((team) => (
                      <button
                        key={team.value}
                        type="button"
                        className={`${styles.campaignTeamFilterOption} ${filters.selectedTeam === team.value ? styles.campaignTeamFilterOptionActive : ""}`}
                        onClick={() => handleSelectTeam(team.value)}
                      >
                        {team.label}
                      </button>
                    ))
                  ) : (
                    <div className={styles.campaignTeamFilterEmpty}>No teams found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Ticket status</label>
          <select
            className={styles.formInput}
            value={filters.ticketTab}
            onChange={(e) => {
              const ticketTab = e.target.value as CampaignTicketTab;
              onChange({
                ...filters,
                ticketTab,
                ...(ticketTab
                  ? {}
                  : {
                      datePreset: null,
                      customStartDate: "",
                      customEndDate: "",
                    }),
              });
            }}
          >
            {CAMPAIGN_TICKET_TAB_OPTIONS.map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filters.ticketTab && (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Event date</label>
          <div className={styles.campaignDateFilterRow}>
            <button
              type="button"
              className={`${styles.campaignDateFilterBtn} ${filters.datePreset === "today" ? styles.campaignDateFilterBtnActive : ""}`}
              onClick={() => applyDatePreset("today")}
            >
              Today ({getTodayStr()})
            </button>
            <button
              type="button"
              className={`${styles.campaignDateFilterBtn} ${filters.datePreset === "tomorrow" ? styles.campaignDateFilterBtnActive : ""}`}
              onClick={() => applyDatePreset("tomorrow")}
            >
              Tomorrow ({getTomorrowStr()})
            </button>
            <button
              type="button"
              className={`${styles.campaignDateFilterBtn} ${filters.datePreset === "next7days" ? styles.campaignDateFilterBtnActive : ""}`}
              onClick={() => applyDatePreset("next7days")}
            >
              Next 7 days (through {getNext7DaysEndStr()})
            </button>
            <button
              type="button"
              className={`${styles.campaignDateFilterBtn} ${filters.datePreset === "custom" ? styles.campaignDateFilterBtnActive : ""}`}
              onClick={() => update({ datePreset: "custom" })}
            >
              Custom range
            </button>
            <button
              type="button"
              className={styles.campaignDateFilterBtn}
              onClick={() =>
                update({
                  datePreset: null,
                  customStartDate: "",
                  customEndDate: "",
                })
              }
            >
              Any date
            </button>
          </div>

          {filters.datePreset === "custom" && (
            <div className={styles.campaignFilterGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Start date</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={filters.customStartDate}
                  onChange={(e) => update({ customStartDate: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>End date</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={filters.customEndDate}
                  onChange={(e) => update({ customEndDate: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className={styles.campaignFilterSummary}>
        <span>Team: {selectedTeamLabel}</span>
        <span>Status: {filters.ticketTab || "Any"}</span>
        <span>Date: {dateLabel}</span>
      </div>

      <div className={styles.campaignFilterActions}>
        <button
          type="button"
          className={styles.previewBtn}
          onClick={onApplyFilters}
          disabled={isResolving || !hasActiveCampaignRecipientFilters(filters)}
        >
          {isResolving ? "Finding users..." : "Apply filters"}
        </button>
        <button type="button" className={styles.campaignFilterClearBtn} onClick={onClearFilters}>
          Clear
        </button>
      </div>

      {resolveError && <div className={styles.campaignFilterError}>{resolveError}</div>}

      {matchingCount !== null && (
        <div className={styles.campaignFilterResults}>
          <strong>{matchingCount}</strong> matching user(s)
          {eligibleCount !== null && (
            <>
              {" "}
              · <strong>{eligibleCount}</strong> eligible for {channel === "email" ? "email" : "push"}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignRecipientFilterPanel;
